import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { UserRoleProvider, useUserRole } from "./context/UserRoleContext";
import { BarProvider } from "./context/BarContext";
import { ArtistProvider } from "./context/ArtistContext";
import { NotificationProvider } from "./context/NotificationContext";
import { MessagingProvider } from "./context/MessagingContext";
import ProtectedRoute from "./components/common/ProtectedRoute";
import RoleBasedRoute from "./components/common/RoleBasedRoute";
import ScrollToTop from "./components/common/ScrollToTop";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";

// Role-based route components
import ArtistRoutes from "./features/artist/ArtistRoutes";
import BarOwnerRoutes from "./features/bar-owner/BarOwnerRoutes";
import BarAdminRoutes from "./features/bar-admin/BarAdminRoutes";

function App() {
  return (
    <AuthProvider>
      <UserRoleProvider>
        <BarProvider>
          <ArtistProvider>
            <NotificationProvider>
              <MessagingProvider>
              <Router>
                <ScrollToTop />
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<Landing />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />

                  {/* Role-Based Protected Routes */}
                  <Route
                    path="/artist/*"
                    element={
                      <RoleBasedRoute allowedRole="artist">
                        <ArtistRoutes />
                      </RoleBasedRoute>
                    }
                  />

                  <Route
                    path="/bar-owner/*"
                    element={
                      <RoleBasedRoute allowedRole="bar_owner">
                        <BarOwnerRoutes />
                      </RoleBasedRoute>
                    }
                  />

                  <Route
                    path="/bar-admin/*"
                    element={
                      <RoleBasedRoute allowedRole="bar_admin">
                        <BarAdminRoutes />
                      </RoleBasedRoute>
                    }
                  />

                  {/* Legacy dashboard route - redirects to role-based dashboard */}
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <RoleBasedRedirect />
                      </ProtectedRoute>
                    }
                  />

                  {/* Redirect unknown routes */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Router>
              </MessagingProvider>
            </NotificationProvider>
          </ArtistProvider>
        </BarProvider>
      </UserRoleProvider>
    </AuthProvider>
  );
}

// Component to redirect to appropriate dashboard based on role
const RoleBasedRedirect = () => {
  const { currentUser } = useAuth();
  const { userRole, loading, error } = useUserRole();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Do not send a signed-in user to /login just because /api/auth/user was slow or failed once.
  if (currentUser && !userRole) {
    return (
      <div className="loading-container">
        <p>We couldn&apos;t load your profile. Please try again.</p>
        {error && <p className="error-message">{error}</p>}
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    );
  }

  switch (userRole) {
    case "artist":
      return <Navigate to="/artist" replace />;
    case "bar_owner":
      return <Navigate to="/bar-owner" replace />;
    case "bar_admin":
      return <Navigate to="/bar-admin" replace />;
    default:
      return <Navigate to="/login" replace />;
  }
};

export default App;
